RivalIQ.apps.Insights.module('data', function (data, App, Backbone, Marionette, $, _) {

    'use strict';

    // sorts assuming the values returned by the valueFunction for a and b are numbers
    function numericValueSort(valueFunction) {
        return function (order, a, b) {
            let value;

            const aVal = valueFunction(a);
            const bVal = valueFunction(b);

            if (isNaN(aVal)) {
                value = -1;
            } else if (isNaN(bVal)) {
                value = 1;
            } else {
                value = aVal - bVal;
            }

            return order * value;
        };
    }

    const Metric = Backbone.Model.extend({
        toColumns: function () {

            const me = this;
            const columns = RivalIQ.state.landscape.get('columns');

            const toColumn = function (parent, metric) {

                const column = _.defaults(_.omit(metric, 'columnName', 'items', 'information'), {
                    sort: metric.property,
                    cellClassName: 'text-center'
                });

                column.omit = (_.has(columns, metric.id) ?
                    columns[metric.id] : (_.has(metric, 'omit') ? _.result(metric, 'omit') : true));

                if (_.has(metric, 'columnName')) {

                    column.name = (_.isFunction(metric.columnName) ?
                        metric.columnName(parent, metric) : metric.columnName);
                }

                return column;
            };

            return _.reduce(me.toArray(), function (memo, metric) {

                const items = metric.items;
                return memo.concat(toColumn(null, metric), (items ? _.map(items, _.partial(toColumn, metric)) : []));
            }, []);
        },

        toArray: function () {

            const me = this;
            const data = me.toJSON();
            return (me.has('items') ? [data] : _.values(data));
        }
    });

    const MetricGrouping = Backbone.Model.extend({

        initialize: function (model) {
            const me = this;

            const metrics = model.metrics;
            _.each(metrics, function f(metric) {

                if (_.isArray(metric)) {

                    return _.each(metric, f);
                }

                // for metrics with computed values, add in a sort function that calls out to get the value and compares
                // using that.
                if (metric.valueSortFunction) {
                    let valueFunction = metric.value;

                    if (!valueFunction) {
                        valueFunction = function (v) {
                            return _.get(v, metric.property);
                        };
                    }

                    metric.sort = metric.valueSortFunction(valueFunction);
                    delete metric.valueSortFunction;
                }

                _.each(metric.items, f);
            });

            me.metricCollection = new Backbone.Collection(metrics, {
                model: Metric
            });
        },

        toColumns: function () {

            const me = this;

            return me.metricCollection.reduce(function (memo, metric) {
                return memo.concat(metric.toColumns());
            }, []);
        }
    });

    const InsightsPostMetrics = Backbone.Collection.extend({
        model: MetricGrouping,

        toColumns: function () {

            const me = this;

            return me.reduce(function (memo, metricGrouping) {
                return memo.concat(metricGrouping.toColumns());
            }, []);
        }
    });

    const Columns = Backbone.Collection.extend({

        getActiveIds: function () {
            const me = this;
            return _.reduce(me.toJSON(), function (memo, column) {
                if (_.result(column, 'omit') !== true) {
                    memo.push(column.id);
                }

                return memo;
            }, []);
        }
    });

    data.getFacebookInsightsPostMetrics = function () {
        if (data.facebookInsightsPostMetrics) {
            return data.facebookInsightsPostMetrics;
        }

        // turn a handful of semanticIds into a structure that the MetricsGroup widget can consume
        function getMetricDescription(semanticId, items, filterFcn) {
            const metricDesc = RivalIQ.metrics.descriptions[semanticId];
            const desc = _.omit(metricDesc, 'description', 'format', 'semanticId');

            if (metricDesc.format) {
                desc.formatter = metricDesc.format;
            }

            if (metricDesc.description) {
                desc.information = {
                    content: metricDesc.description
                };
            }

            desc.items = _(items)
                .map(function (item) {
                    return getMetricDescription(item, null, filterFcn);
                })
                .compact()
                .value();

            if (metricDesc.previousId) {
                desc.id = metricDesc.previousId;
            }

            if (filterFcn && !filterFcn(desc)) {
                return null;
            }

            return desc;
        }

        const adsColumnFilter = function () {
            return RivalIQ.state.account.channelDisplay(RivalIQ.enums.sourceMask.FACEBOOK_BOOSTED_POST_INSIGHTS);
        };

        const fbInsightsPostMetrics = [
            {
                categoryName: 'Reach',
                columnIdx: 0,
                metrics: [
                    [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_REACH', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_REACH',
                            'FACEBOOK_INSIGHTS_POST_PAID_REACH',
                            'FACEBOOK_INSIGHTS_POST_VIRAL_REACH'
                        ]),
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_IMPRESSIONS', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_IMPRESSIONS',
                            'FACEBOOK_INSIGHTS_POST_PAID_IMPRESSIONS',
                            'FACEBOOK_INSIGHTS_POST_VIRAL_IMPRESSIONS'
                        ])
                    ],
                    getMetricDescription('FACEBOOK_INSIGHTS_POST_FAN_REACH')
                ]
            },
            {
                categoryName: 'Consumptions',
                columnIdx: 0,
                metrics: [
                    _.compact([
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_CONSUMPTIONS', [
                            'FACEBOOK_INSIGHTS_POST_CONSUMPTIONS_LINK_CLICKS',
                            'FACEBOOK_INSIGHTS_POST_CONSUMPTIONS_PHOTO_VIEWS',
                            'FACEBOOK_INSIGHTS_POST_CONSUMPTIONS_VIDEO_PLAYS',
                            'FACEBOOK_INSIGHTS_POST_CONSUMPTIONS_OTHER_CLICKS'
                        ]),
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_PAID_CONSUMPTIONS', [
                            'FACEBOOK_INSIGHTS_POST_PAID_LINK_CLICKS',
                            'FACEBOOK_INSIGHTS_POST_PAID_PHOTO_VIEWS',
                            'FACEBOOK_INSIGHTS_POST_PAID_VIDEO_PLAYS',
                            'FACEBOOK_INSIGHTS_POST_PAID_OTHER_CLICKS'
                        ], adsColumnFilter)
                    ])
                ]
            },
            {
                categoryName: 'Engaged Users',
                columnIdx: 0,
                metrics: [
                    [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_ENGAGEMENT_RATE', [
                            'FACEBOOK_INSIGHTS_POST_ENGAGED_USERS',
                            'FACEBOOK_INSIGHTS_POST_ENGAGED_FANS',
                            'FACEBOOK_INSIGHTS_POST_ENGAGED_USERS_OTHER'
                        ])
                    ]
                ]
            },

            {
                categoryName: 'General',
                columnIdx: 0,
                metrics: [
                    _.compact([
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_LIVE_VIDEO_VIEWS'),
                        getMetricDescription('FACEBOOK_INSIGHTS_BOOSTED_TOTAL_SPEND', null, adsColumnFilter),
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_ADMIN_CREATOR_NAME')
                    ])
                ]
            },
            {
                categoryName: 'Social Engagement',
                columnIdx: 1,
                metrics: _.compact([
                    [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_AND_SHARES_ENGAGEMENT_TOTAL', [
                            'FACEBOOK_INSIGHTS_POST_AND_SHARES_REACTIONS',
                            'FACEBOOK_INSIGHTS_POST_AND_SHARES_COMMENTS',
                            'FACEBOOK_INSIGHTS_POST_AND_SHARES_SHARES'
                        ]),
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_ENGAGEMENT_TOTAL', [
                            'FACEBOOK_INSIGHTS_POST_REACTIONS',
                            'FACEBOOK_INSIGHTS_POST_COMMENTS',
                            'FACEBOOK_INSIGHTS_POST_SHARES'
                        ])
                    ],
                    (adsColumnFilter() ? [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_PAID_ENGAGEMENT', [
                            'FACEBOOK_INSIGHTS_POST_PAID_REACTIONS',
                            'FACEBOOK_INSIGHTS_POST_PAID_COMMENTS',
                            'FACEBOOK_INSIGHTS_POST_PAID_SHARES'
                        ]),
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_ORGANIC_ENGAGEMENT', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_REACTIONS',
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_COMMENTS',
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_SHARES'
                        ])
                    ] : null),
                    [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_REACTIONS', [
                            'FACEBOOK_INSIGHTS_POST_REACTIONS_LIKE',
                            'FACEBOOK_INSIGHTS_POST_REACTIONS_LOVE',
                            'FACEBOOK_INSIGHTS_POST_REACTIONS_HAHA',
                            'FACEBOOK_INSIGHTS_POST_REACTIONS_WOW',
                            'FACEBOOK_INSIGHTS_POST_REACTIONS_SORRY',
                            'FACEBOOK_INSIGHTS_POST_REACTIONS_ANGRY'
                        ])
                    ],
                    getMetricDescription('FACEBOOK_INSIGHTS_SOCIAL_ENGAGEMENT_RATE')
                ])
            },

            {
                categoryName: 'Video Metrics',
                columnIdx: 1,
                metrics: [
                    [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_VIDEO_AVG_WATCH_LENGTH', [
                            'FACEBOOK_INSIGHTS_POST_VIDEO_LENGTH',
                            'FACEBOOK_INSIGHTS_POST_VIDEO_AVG_PERCENT_VIEWED'
                        ]),
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_VIDEO_VIEWS', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_VIDEO_VIEWS',
                            'FACEBOOK_INSIGHTS_POST_PAID_VIDEO_VIEWS'
                        ])
                    ],
                    [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_VIDEO_30_SECOND_VIEWS', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_VIDEO_30_SECOND_VIEWS',
                            'FACEBOOK_INSIGHTS_POST_PAID_VIDEO_30_SECOND_VIEWS'
                        ]),
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_VIDEO_30_SECOND_VIEW_RATE', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_VIDEO_30_SECOND_VIEW_RATE',
                            'FACEBOOK_INSIGHTS_POST_PAID_VIDEO_30_SECOND_VIEW_RATE'
                        ])
                    ],
                    [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_VIDEO_95_PERCENT_VIEWS', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_VIDEO_95_PERCENT_VIEWS',
                            'FACEBOOK_INSIGHTS_POST_PAID_VIDEO_95_PERCENT_VIEWS'
                        ]),
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_VIDEO_95_PERCENT_VIEW_RATE', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_VIDEO_95_PERCENT_VIEW_RATE',
                            'FACEBOOK_INSIGHTS_POST_PAID_VIDEO_95_PERCENT_VIEW_RATE'
                        ])
                    ],
                    [
                        getMetricDescription('FACEBOOK_INSIGHTS_POST_TOTAL_VIDEO_10_SECOND_VIEWS', [
                            'FACEBOOK_INSIGHTS_POST_ORGANIC_VIDEO_10_SECOND_VIEWS',
                            'FACEBOOK_INSIGHTS_POST_PAID_VIDEO_10_SECOND_VIEWS'
                        ]),
                        getMetricDescription('FACEBOOK_INSIGHTS_VIDEO_VIEW_RATE')
                    ]
                ]
            }
        ];

        const facebookInsightsPostMetrics = new InsightsPostMetrics(fbInsightsPostMetrics, {
            metricIdPrefix: 'facebook_post_metric_'
        });

        facebookInsightsPostMetrics.types = {};

        /**
         * Gets metrics from the specified types.
         * @param [types] An array of types for which to retrieve metrics.
         */
        facebookInsightsPostMetrics.getMetrics = function (types) {

            types = (types || _.keys(facebookInsightsPostMetrics.types));

            const metrics = _.reduce(types, function (memo, type) {

                const typeCollection = facebookInsightsPostMetrics.types[type];
                return (typeCollection ? memo.concat(typeCollection.toArray()) : memo);
            }, []);

            return new Backbone.Collection(metrics);
        };

        /**
         * Gets columns from all available post metrics.
         * NOTE: LOADS LAZY, CACHES RESULT VIA MEMOIZE MAKING PSUEDO SINGLETON
         */
        facebookInsightsPostMetrics.getColumns = _.memoize(function () {

            const columns = _.reduce(_.keys(facebookInsightsPostMetrics.types), function (memo, type) {

                const typeCollection = facebookInsightsPostMetrics.types[type];
                return (typeCollection ? memo.concat(typeCollection.toColumns()) : memo);
            }, facebookInsightsPostMetrics.toColumns());

            const collection = new Columns(columns);

            collection.toSettings = function () {

                return collection.reduce(function (memo, column) {

                    if (!_.result(column.attributes, 'omit')) {

                        memo[column.id] = false;
                    }

                    return memo;
                }, {});
            };

            return collection;
        });

        data.facebookInsightsPostMetrics = facebookInsightsPostMetrics;
        return data.facebookInsightsPostMetrics;
    };

    data.facebookInsightsPostTypes = function () {
        return [
            {
                name: 'All Media',
                type: null
            },
            {
                name: 'Link',
                type: 'link'
            },
            {
                name: 'Status',
                type: 'status'
            },
            {
                name: 'Photo',
                type: 'photo'
            },
            {
                name: 'Reel',
                type: 'reel'
            },
            {
                name: 'Video',
                type: 'video'
            },
            {
                name: 'Live Video',
                type: 'live_video'
            },
            {
                name: 'Offer',
                type: 'offer'
            }
        ];
    };

    data.linkedInInsightsPostTypes = function () {
        return [
            {
                name: 'All Media',
                type: null
            },
            {
                name: 'Article',
                type: 'ARTICLE'
            },
            {
                name: 'Photo',
                type: 'PHOTO'
            },
            {
                name: 'None',
                type: 'NONE'
            },
            {
                name: 'Rich',
                type: 'RICH'
            },
            {
                name: 'Video',
                type: 'VIDEO'
            },
            {
                name: 'Learning',
                type: 'LEARNING'
            },
            {
                name: 'Job',
                type: 'JOB'
            },
            {
                name: 'Question',
                type: 'QUESTION'
            },
            {
                name: 'Answer',
                type: 'ANSWER'
            },
            {
                name: 'Carousel',
                type: 'CAROUSEL'
            },
            {
                name: 'Topic',
                type: 'TOPIC'
            }
        ];
    };

    function watchedMinuteFormatter(minutes) {
        return RivalIQ.formatter.formats.abbreviated(minutes, {
            decimals: 2
        });
    }

    data.getLinkedInInsightsPostMetrics = function () {
        if (data.linkedInInsightsPostMetrics) {
            return data.linkedInInsightsPostMetrics;
        }
        const linkedInInsightsPostMetrics = new InsightsPostMetrics([
            {
                categoryName: 'Update Metrics',
                columnIdx: 0,
                metrics: [{
                    'id': 'linked_in_update_metric_104',
                    name: 'Total Engagement',
                    property: 'li_insights_engagement',
                    sortKey: 'li_insights_engagement',
                    valueSortFunction: numericValueSort,
                    formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.abbreviated),
                    information: {
                        content: 'The total number of likes, clicks, shares, and comments.'
                    },
                    items: [
                        {
                            'id': 'linked_in_update_metric_105',
                            name: 'Likes',
                            property: 'likes',
                            formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.abbreviated),
                            information: {
                                content: 'The number of likes of this update.'
                            }
                        },
                        {
                            'id': 'linked_in_update_metric_106',
                            name: 'Comments',
                            property: 'comments',
                            formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.abbreviated),
                            information: {
                                content: 'The number of comments on this update.'
                            }
                        },
                        {
                            'id': 'linked_in_update_metric_107',
                            name: 'Shares',
                            property: 'shares',
                            formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.abbreviated),
                            information: {
                                content: 'The number of shares of this update.'
                            }
                        },
                        {
                            'id': 'linked_in_update_metric_108',
                            name: 'Update Clicks',
                            property: 'clicks',
                            formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.abbreviated),
                            information: {
                                content: 'The number of clicks of this update.'
                            }
                        }
                    ]
                },
                {
                    id: 'linked_in_update_metric_114',
                    name: 'Engagement Rate',
                    property: 'li_insights_engagement_rate',
                    sortKey: 'li_insights_engagement_rate',
                    valueSortFunction: numericValueSort,
                    formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.percentAbsolute),
                    information: {
                        content: 'The total number of likes, clicks, shares, and comments, divided by the number of impressions on this update.'
                    },
                    items: []
                },
                {
                    id: 'linked_in_update_metric_113',
                    name: 'Impressions',
                    property: 'impressions',
                    sortKey: 'impressions',
                    valueSortFunction: numericValueSort,
                    formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.abbreviated),
                    information: {
                        content: 'The number of impressions on this update.'
                    },
                    items: []
                }]
            },
            {
                categoryName: 'Video Metrics',
                columnIdx: 1,
                applicableForType: 'video',
                metrics: [{
                    id: 'linked_in_update_metric_110',
                    name: 'Video Views',
                    property: 'video_views',
                    formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.abbreviated),
                    information: {
                        content: 'The number of times this video was watched for more than 3 seconds.'
                    },
                    items: [
                        {
                            id: 'linked_in_update_metric_111',
                            name: 'Video Viewers',
                            property: 'video_viewers',
                            formatter: RivalIQ.formatter.naFalsyDataWrapper(RivalIQ.formatter.formats.abbreviated),
                            information: {
                                content: 'The number of unique people who watched this video for more than 3 seconds.'
                            }
                        }, {
                            id: 'linked_in_update_metric_112',
                            name: 'Minutes Watched',
                            property: 'video_watched_minutes',
                            formatter: RivalIQ.formatter.naFalsyDataWrapper(watchedMinuteFormatter),
                            information: {
                                content: 'The total minutes this video has been watched, including views less than 3 seconds.'
                            },
                            items: []
                        }
                    ]
                }]
            }
        ], {
            metricIdPrefix: 'linked_in_update_metric_'
        });

        /**
         * Filters metrics from the specified type.
         * @param type The type for which to retrieve metrics.
         */
        linkedInInsightsPostMetrics.getForType = function (type) {
            const me = this;

            type = (type || '').toLowerCase();
            return new InsightsPostMetrics(me.filter(function (metrics) {
                const applicableForType = metrics.get('applicableForType');
                return !applicableForType || applicableForType === type;
            }));
        };

        /**
         * Gets columns from all available post metrics.
         * NOTE: LOADS LAZY, CACHES RESULT VIA MEMOIZE MAKING PSUEDO SINGLETON
         */
        linkedInInsightsPostMetrics.getColumns = _.memoize(function () {

            const columns = linkedInInsightsPostMetrics.toColumns();

            const collection = new Columns(columns);

            collection.toSettings = function () {

                return collection.reduce(function (memo, column) {

                    if (!_.result(column.attributes, 'omit')) {

                        memo[column.id] = false;
                    }

                    return memo;
                }, {});
            };

            return collection;
        });

        data.linkedInInsightsPostMetrics = linkedInInsightsPostMetrics;
        return linkedInInsightsPostMetrics;
    };
});
